import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Polygon, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import GeoSearchAutocomplete from "@/components/GeoSearchAutocomplete";
import { FlyTo } from "@/components/MapCore";
import { useAssistantPageTips } from "@/components/ContextAssistant";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, Camera, Loader2, X, Locate, Undo2, MapPin, Pencil } from "lucide-react";
import VideoUploader from "@/components/VideoUploader";

// TODO: replace with the official custom marker asset when provided.
// Placeholder marker path: /app/frontend/public/img/extraordinary-event-marker.png
const eventMarker = new L.DivIcon({
  className: "",
  html: '<div style="width:28px;height:28px;background:#EF4444;border:3px solid #fff;border-radius:50%;box-shadow:0 4px 10px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:14px">!</div>',
  iconSize: [28, 28], iconAnchor: [14, 14],
});

const EVENT_KINDS = [
  "wildfire", "flood", "river_overflow", "landslide", "storm", "hailstorm",
  "tornado", "heavy_snow", "avalanche", "sea_storm", "heatwave", "coldwave",
];

function ClickToAdd({ onAdd, disabled, active }) {
  useMapEvents({
    click: (e) => { if (!disabled && active) onAdd([e.latlng.lat, e.latlng.lng]); },
  });
  return null;
}

function LocateBtn({ onCenter }) {
  const map = useMap();
  const { t } = useI18n();
  return (
    <button
      type="button"
      data-testid="event-locate-btn"
      onClick={() => {
        if (!navigator.geolocation) { toast.error(t("map_locate_unsupported")); return; }
        navigator.geolocation.getCurrentPosition(
          (p) => { map.flyTo([p.coords.latitude, p.coords.longitude], 13, { duration: 0.6 }); onCenter([p.coords.latitude, p.coords.longitude]); },
          () => toast.error(t("map_geo_denied")),
        );
      }}
      className="absolute top-4 right-4 z-[1000] h-10 px-3 rounded-full bg-white/95 border border-slate-200 shadow-md text-slate-800 text-xs font-semibold inline-flex items-center gap-1.5"
    >
      <Locate size={13} /> {t("map_locate_here")}
    </button>
  );
}

export default function EventCreate() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  useAssistantPageTips([
    { id: "event-scope", text: t("tip_event_scope") },
    { id: "event-polygon", text: t("tip_event_polygon") },
    { id: "event-moderation", text: t("tip_event_moderation") },
  ]);
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("wildfire");
  const [description, setDescription] = useState("");
  const [whatToDo, setWhatToDo] = useState("");
  const [startedAt, setStartedAt] = useState("");
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);  // Phase 4
  const [points, setPoints] = useState([]); // [[lat,lng], ...]
  // Lotto 10 — draw-mode toggle (matita). When ON, taps on the map add
  // polygon vertices; when OFF, taps behave normally (pan/zoom) so the user
  // can freely explore the map on mobile without accidentally adding points.
  const [drawMode, setDrawMode] = useState(false);
  const [center, setCenter] = useState([42.5, 12.5]);
  const [flyCenter, setFlyCenter] = useState(null);
  // Optional territorial pre-selection (Paese / Provincia / Comune).
  // Picking a territory centres the map on its coordinates; the polygon
  // vertices remain untouched — the user still draws the area by clicking.
  const [geoArea, setGeoArea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (!loading && !user) nav("/auth?mode=signin"); }, [user, loading, nav]);

  const isVerified = user && (user.role === "admin" || user.role === "moderator" || user.verified_entity);

  const addPoint = (p) => setPoints((prev) => [...prev, p]);  const undo = () => setPoints((p) => p.slice(0, -1));
  const clearAll = () => setPoints([]);

  // Lotto 9 fix — "Usa area corrente".
  // On mobile the polygon-by-tap flow is nearly impossible: taps get eaten by
  // pan gestures, so Super Admins/verified entities can't publish. This helper
  // generates a ~2km square polygon around the current map centre so the user
  // can publish immediately, then refine with pins later if needed.
  const useCurrentArea = () => {
    const [lat, lng] = center;
    // Approximate 1 km in degrees: 1/111 for lat, and 1/(111*cos(lat)) for lng.
    const d = 1 / 111;
    const dLng = 1 / (111 * Math.max(0.1, Math.cos(lat * Math.PI / 180)));
    setPoints([
      [lat + d, lng - dLng],
      [lat + d, lng + dLng],
      [lat - d, lng + dLng],
      [lat - d, lng - dLng],
    ]);
    toast.success(t("event_area_used_current") || "Area di ~2 km generata attorno al centro mappa");
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, data.path]);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload fallito"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error(t("event_title_required"));
    if (points.length < 3) return toast.error(t("event_polygon_required"));
    setSaving(true);
    try {
      // GeoJSON polygon needs to close and be [lng, lat]
      const ring = points.map(([lat, lng]) => [lng, lat]);
      ring.push(ring[0]);
      const payload = {
        title: title.trim(),
        kind, description: description.trim(),
        photos, videos, geometry: { type: "Polygon", coordinates: [ring] },
        started_at: startedAt || null,
      };
      // «Cosa fare ora» is Admin/Moderator/Ente verificato only. Never even
      // include the field in the request body for regular citizens.
      if (isVerified) {
        payload.what_to_do = whatToDo.trim() || null;
      }
      const { data } = await api.post("/events", payload);
      toast.success(data.state === "published" ? t("event_published_ok") : t("event_proposal_sent"));
      nav(`/events/${data.event_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || t("event_generic_error")); }
    finally { setSaving(false); }
  };

  const polygonLatLng = useMemo(() => points, [points]);

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="event-create-page">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-6 pb-4">
        <BackButton testid="event-back-btn" />
      </div>
      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-12">
        <div className="text-xs uppercase tracking-[0.25em] text-red-700 font-semibold mb-1">{t("event_extraordinary_kicker")}</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">{t("event_new_proposal")}</h1>
        <p className="text-sm text-slate-600 mb-6 max-w-2xl">
          {t("event_intro_line")} {isVerified ? t("event_intro_verified_pub") : t("event_intro_review_pub")}
        </p>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("event_title_label")} *</label>
              <Input data-testid="event-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("event_title_placeholder")} className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("event_kind_label")} *</label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="event-kind" className="mt-1 rounded-xl h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVENT_KINDS.map((k) => (
                    <SelectItem key={k} value={k} data-testid={`event-kind-${k}`}>{t(`event_kind_${k}`) || k}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("description")}</label>
              <Textarea data-testid="event-desc" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("event_desc_placeholder")} className="mt-1 rounded-xl" />
            </div>
            {isVerified && (
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("event_whattodo_admin_only")}</label>
                <Textarea data-testid="event-whattodo" rows={3} value={whatToDo} onChange={(e) => setWhatToDo(e.target.value)} placeholder={t("event_whattodo_placeholder")} className="mt-1 rounded-xl" />
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("event_started_label")}</label>
              <Input data-testid="event-started" type="datetime-local" value={startedAt} onChange={(e) => setStartedAt(e.target.value)} className="mt-1 rounded-xl" />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("event_photos_label")}</label>
              <div className="mt-1 grid grid-cols-3 md:grid-cols-4 gap-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200" data-testid={`event-photo-${i}`}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="p" className="w-full h-full object-cover" />
                    <button onClick={() => setPhotos((ps) => ps.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={12} /></button>
                  </div>
                ))}
                <label className="aspect-square rounded-lg border-2 border-dashed border-emerald-300 flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700" data-testid="event-photo-add">
                  {uploading ? <Loader2 className="animate-spin" size={18} /> : <Camera size={18} />}
                  <span className="text-[10px] mt-1 text-center">{t("add_photo_short")}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }} />
                </label>
              </div>
              <div className="mt-3">
                <VideoUploader value={videos} onChange={setVideos} testidPrefix="event-video" />
                <div className="text-[10px] text-slate-500 mt-1">{t("video_privacy_notice")}</div>
              </div>
            </div>

            <Button
              data-testid="event-submit"
              onClick={submit}
              disabled={saving || !title.trim() || points.length < 3}
              className="w-full h-12 rounded-full bg-red-600 hover:bg-red-700 text-white hover:text-white disabled:opacity-60 font-semibold"
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <AlertTriangle className="mr-2" size={16} />}
              {isVerified ? t("event_publish_btn") : t("event_send_proposal_btn")}
            </Button>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("event_area_involved")} *</div>
            <div className="text-xs text-slate-600 mb-2">{t("event_area_hint")}</div>
            <div className="mb-2">
              <GeoSearchAutocomplete
                testid="event-geo-search"
                value={geoArea}
                onChange={(area) => {
                  setGeoArea(area);
                  if (area && typeof area.lat === "number" && typeof area.lng === "number") {
                    setCenter([area.lat, area.lng]);
                    setFlyCenter([area.lat, area.lng]);
                  }
                }}
                placeholder={t("country_or_province")}
              />
              {geoArea && (
                <div className="mt-1 text-[10px] text-emerald-800 font-semibold" data-testid="event-geo-selected">
                  {t("event_territory_prefix")}: {geoArea.display}
                </div>
              )}
            </div>
            <div className="rounded-2xl overflow-hidden border-2 border-slate-200 relative h-[420px]" data-testid="event-map">
              <MapContainer center={center} zoom={6} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
                <ClickToAdd onAdd={addPoint} disabled={saving} active={drawMode} />
                <LocateBtn onCenter={setCenter} />
                <FlyTo center={flyCenter} zoom={10} />
                {points.map((p, i) => (
                  <Marker key={i} position={p} icon={eventMarker} />
                ))}
                {points.length >= 3 && (
                  <Polygon positions={polygonLatLng} pathOptions={{ color: "#EF4444", weight: 2, fillColor: "#EF4444", fillOpacity: 0.15 }} />
                )}
              </MapContainer>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <Button size="sm" onClick={() => setDrawMode(m => !m)} disabled={saving} data-testid="event-poly-draw-toggle"
                className={`rounded-full h-8 ${drawMode ? "bg-red-600 hover:bg-red-700 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
                <Pencil size={12} className="mr-1" /> {drawMode ? (t("event_draw_stop") || "Ferma disegno") : (t("event_draw_start") || "Disegna area")}
              </Button>
              <Button size="sm" variant="outline" onClick={useCurrentArea} disabled={saving} className="rounded-full h-8" data-testid="event-poly-use-current">
                <MapPin size={12} className="mr-1" /> {t("event_use_current_area") || "Usa area corrente (~2 km)"}
              </Button>
              <Button size="sm" variant="outline" onClick={undo} disabled={!points.length} className="rounded-full h-8" data-testid="event-poly-undo">
                <Undo2 size={12} className="mr-1" /> {t("event_undo_last")}
              </Button>
              <Button size="sm" variant="outline" onClick={clearAll} disabled={!points.length} className="rounded-full h-8" data-testid="event-poly-clear">
                <X size={12} className="mr-1" /> {t("event_clear_all")}
              </Button>
              <div className="text-xs text-slate-500 ml-auto flex items-center gap-1" data-testid="event-poly-count">
                <MapPin size={12} /> {points.length} {t("event_points_count_suffix")}
              </div>
            </div>
            {drawMode && (
              <div className="mt-2 rounded-xl bg-red-50 border border-red-200 p-2 text-[11px] text-red-900" data-testid="event-poly-draw-active">
                {t("event_draw_active_hint") || "Modalità disegno ATTIVA — tocca la mappa per aggiungere vertici. Servono almeno 3 punti. Ripremi \"Ferma disegno\" quando finito."}
              </div>
            )}
            {points.length < 3 && !drawMode && (
              <div className="mt-2 rounded-xl bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900" data-testid="event-poly-hint">
                {t("event_polygon_hint_manual") || "Attiva \"Disegna area\" e tocca almeno 3 punti sulla mappa per delimitare l'area. In alternativa usa \"Usa area corrente\" per un poligono automatico di 2 km."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
